"use client";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { authService, AuthEventType } from "../services/AuthService";
import { User } from "../api/auth";
import { isHandyApp } from "../utils/device";

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  login: (target: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  setAuthenticated: (authenticated: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * 认证 Provider
 * 管理用户登录状态，支持游客模式
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * 检查认证状态
   * 从 URL 或 Cookie 中检测 Token
   */
  const checkAuth = useCallback(async () => {
    setIsLoading(true);

    try {
      // 1. 检查 URL 中是否有 ticket（从官网登录回来）
      const url = new URL(window.location.href);
      const ticket = url.searchParams.get("ticket");

      if (ticket) {
        // 有 ticket，说明是从官网登录回来的
        const { login } = await import("../api/auth");
        const res = await login(ticket);

        // 保存 Token 和用户信息
        authService.setToken(res.accessToken, res);
        setUser(res.cusInfo);
        setIsAuthenticated(true);

        // 清除 URL 中的 ticket 参数
        url.searchParams.delete("ticket");
        url.searchParams.delete("redirect");
        window.history.replaceState({}, "", url.toString());
      } else {
        // 2. 检查是否有已存在的 Token
        const existingToken = authService.getToken();

        if (existingToken) {
          // 重新调用setToken确保登录token被设置
          authService.setToken(existingToken);
          // 有 Token，尝试续期（如果需要）
          await authService.renewalToken();

          // 从 LocalStorage 获取用户信息
          const loginInfo = localStorage.getItem("login_info");
          if (loginInfo) {
            try {
              const info = JSON.parse(loginInfo);
              setUser(info.cusInfo);
              setIsAuthenticated(true);
            } catch (error) {
              console.error("Failed to parse login info:", error);
            }
          } else {
            setIsAuthenticated(true);
          }
        } else {
          // 如果是在handy app中，走无感知登录
          if (isHandyApp()) {
            const res = await authService.fetchHandyAppLogin();
            if (res) {
              setUser(res.cusInfo);
              setIsAuthenticated(true);
            } else {
              setIsAuthenticated(false);
              setUser(null);
            }
            return;
          }
          // 无 Token，游客模式
          setIsAuthenticated(false);
          setUser(null);
        }
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * 主动登录
   * 用户点击登录按钮时调用
   */
  const login = useCallback(async (target: string) => {
    // 调用 AuthService 的登录方法
    // 会自动重定向到官网登录页
    await authService.login(false, target);
  }, []);

  /**
   * 登出
   */
  const logout = useCallback(() => {
    authService.clearToken();
    setIsAuthenticated(false);
    setUser(null);

    // 刷新页面
    window.location.reload();
  }, []);

  /**
   * 手动设置认证状态
   * 用于 HandyApp 场景，在获取到用户信息后更新登录态
   */
  const setAuthenticated = useCallback((authenticated: boolean) => {
    setIsAuthenticated(authenticated);
  }, []);

  // 初始化时检查认证状态
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // 监听 AuthService 的登出事件
  useEffect(() => {
    const handleLogout = () => {
      setIsAuthenticated(false);
      setUser(null);
    };

    const handleTokenExpired = () => {
      console.warn("Token expired, user logged out");
      setIsAuthenticated(false);
      setUser(null);
    };

    // 订阅事件
    const unsubscribeLogout = authService.on(
      AuthEventType.LOGOUT,
      handleLogout,
    );
    const unsubscribeTokenExpired = authService.on(
      AuthEventType.TOKEN_EXPIRED,
      handleTokenExpired,
    );

    // 清理订阅
    return () => {
      unsubscribeLogout();
      unsubscribeTokenExpired();
    };
  }, []);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    isLoading,
    login,
    logout,
    checkAuth,
    setAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * 使用认证 Hook
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

/**
 * 检查是否已登录
 */
export function useIsAuthenticated() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
}

/**
 * 获取当前用户信息
 */
export function useCurrentUser() {
  const { user } = useAuth();
  return user;
}

/**
 * 主动登录 Hook
 */
export function useLogin() {
  const { login } = useAuth();
  return login;
}

/**
 * 登出 Hook
 */
export function useLogout() {
  const { logout } = useAuth();
  return logout;
}
